/** CRM tab (prove-out) — read-only look-up loop over the governed relationship graph.
 *
 * URL model (navigation-deeplink-spec):
 *   ?q=     debounced search text — replace-mode view param (typing ≠ Back-steps)
 *   ?sel=   ONE selection param, `person:7` / `org:9` / `msg:5` (push + {emberSel}
 *           marker via useSelectionParam). A single param means profile→profile
 *           navigation (person → their org → a message) REPLACES the selection value
 *           on a fresh pushed entry, so Back walks the chain one level at a time and
 *           there is no multi-param precedence ambiguity. Plain tab clicks drop all
 *           params (useTabRouter §3.4).
 *   ?msg=   Pillar F.3 — a message opened as a MODAL over whatever is below it. A second
 *           selection axis, exactly the `note` overlay's model: pushed, so Back closes the
 *           modal onto the surface it opened from, and shareable, so "open this mail" is
 *           still a deep link (it always was, as `sel=msg:`). `?sel=msg:N` is untouched and
 *           still renders the FULL page — that is where the modal's "Open full view ↗" goes.
 *
 * The search box is the codebase's first DEBOUNCED URL-backed q: local input state
 * debounces into the URL (300ms, replace), and every row-open FLUSHES the pending write
 * first so the final keystrokes land on the LIST history entry, not the selection's
 * (useUrlState.ts NOTE / spec §12 finding 2).
 */
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import styles from '../crm/crm.module.css';
import { ApiError } from '../../api/client';
import { EMBER_NAV_EVENT, useSelectionParam, useViewParam } from '../../hooks/navigation/useUrlState';
import { useCrmOpportunitySearch, useCrmSearch } from '../../hooks/useCrm';
import type { CrmContentHit, CrmDirectoryHit, CrmLookupResult } from '../../types';
import { CrmListGrid } from '../crm/CrmListGrid';
import { CrmNoteOverlay } from '../crm/CrmNoteOverlay';
import { CrmListsHome } from '../crm/CrmListsHome';
import { pushRecentList } from '../crm/crmListsIndex';
import { CrmMessageModal } from '../crm/CrmMessageModal';
import { CrmMessageView } from '../crm/CrmMessageView';
import { CrmProfile } from '../crm/CrmProfile';
import { fmtShortDate, isPersonSubtype, subtypeLabel } from '../crm/crmFormat';
import { matchTier, orderByBestMatch, rowTier } from '../crm/crmSearchRank';

type Selection = { kind: 'person' | 'org' | 'msg' | 'list'; id: number };

function parseSel(raw: string | null): Selection | null {
  const match = raw != null ? /^(person|org|msg|list):(\d+)$/.exec(raw) : null;
  if (!match) return null; // garbage/stale param → list renders
  return { kind: match[1] as Selection['kind'], id: parseInt(match[2], 10) };
}

export function CrmPage() {
  const sel = useSelectionParam('sel');
  const selection = parseSel(sel.value);
  // Read `rec` reactively so the §7 cleanup below re-runs when a record is closed (its own close only
  // drops `rec`); CrmListGrid/CrmRecordPanelContainer own the actual open/close.
  const rec = useSelectionParam('rec');

  // Item D §7 cleanup: rec + rtab (+ the subordinate `note` param, D6) are valid only as an open
  // record OVER a list. Strip them when
  //  (a) the selection isn't a list (e.g. "Open full profile" pushed sel=person over an open record); OR
  //  (b) the selection IS a list but `rec` was closed while `rtab`/`note` lingered — the markerless
  //      deep-link/reload close path (useSelectionParam.close) only deletes `rec`, so an orphaned
  //      `rtab=updates`/`note=Z` would re-activate the wrong sub-view or auto-open a stale note on the
  //      NEXT record opened (§7 "closing rec drops rtab"; note is nested under rec, D6-fix #3).
  // Leave them alone only when a record is genuinely open over a list. Replace (no navigation), then
  // resync sibling hooks via the shared nav event. Reactive to rec.value so the close actually fires it.
  useEffect(() => {
    if (selection?.kind === 'list' && rec.value) return;   // a record is genuinely open — valid
    const url = new URL(window.location.href);
    // Only clean when there is record-scoped state (rec/rtab) to tear down. A BARE `note` (no rec/rtab)
    // is a PROFILE note the profile's own Notes section owns — do NOT strip it here, or a profile note
    // deep-link (?sel=person:7&note=Z) would break.
    if (!url.searchParams.has('rec') && !url.searchParams.has('rtab')) return;
    url.searchParams.delete('rec');
    url.searchParams.delete('rtab');
    url.searchParams.delete('note');   // note nested under this record → drop with the record teardown
    url.searchParams.delete('msg');    // F.3: same — a mail modal opened over a record dies with it
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    window.dispatchEvent(new CustomEvent(EMBER_NAV_EVENT));
  }, [selection?.kind, rec.value]);

  // F.3: the mail MODAL axis. `openMsg` now opens the modal; the full page is reached from inside it
  // (and from any pre-existing `?sel=msg:N` link), which is what keeps the deep link alive.
  const msg = useSelectionParam('msg');
  const msgId = msg.value != null && /^\d+$/.test(msg.value) ? parseInt(msg.value, 10) : null;
  const openPerson = (id: number) => sel.open(`person:${id}`);
  const openOrg = (id: number) => sel.open(`org:${id}`);
  const openMsg = (id: number) => msg.open(String(id));
  /**
   * Navigate OUT of the mail modal to a new selection, taking the modal down with it.
   *
   * ORDER MATTERS: `sel.open` pushes a new entry (stamped `{emberSel:'sel'}`) carrying both params, and
   * `msg.close` then sees an entry that is not ITS pushed one, so it takes the synchronous
   * replaceState branch and strips `msg` in place. Closing FIRST would take the `history.back()`
   * branch, which resolves asynchronously via popstate and would race the push.
   *
   * ⚠ The explicit close is REQUIRED, not tidiness (F.2 review MAJOR 2). The §7 teardown below strips
   * `msg` only when there is record-scoped state to tear down — it early-returns when neither `rec`
   * nor `rtab` is present. So on the plain path `?sel=person:7` → open mail (`&msg=5`) → click a
   * participant, `sel` advanced to the new person while `msg=5` survived and the modal re-rendered
   * over the new profile: the click read as a complete no-op to the user.
   */
  const navigateFromModal = (selValue: string) => { sel.open(selValue); msg.close(); };
  const openMsgFullView = (id: number) => navigateFromModal(`msg:${id}`);
  /** The same destination from a surface where NO modal is open — the profile timeline's
   *  "Full view ↗". A plain push selection: `navigateFromModal`'s extra `msg.close()` exists to
   *  strip a `msg` param that is not set here, and closing a param nobody opened would take the
   *  `history.back()` branch and race the push. */
  const openMsgPage = (id: number) => sel.open(`msg:${id}`);
  const openPersonFromModal = (id: number) => navigateFromModal(`person:${id}`);
  const openList = (id: number) => { pushRecentList(id); sel.open(`list:${id}`); };
  // Opportunity search hit (spec Part 3): open the record panel over its parent list's grid —
  // sel + rec compose in the same tick (writeParam reads live location), producing the standard
  // deep-link shape ?sel=list:X&rec=Y. Back pops to the grid, then to the search list.
  const openListRecord = (listId: number, entryId: number) => {
    pushRecentList(listId);
    sel.open(`list:${listId}`);
    rec.open(String(entryId));
  };
  // E4: the grid's latest-note cell opens the note overlay via the `note` param (a SECOND selection
  // axis above `sel`, like the profile Notes section). Rendered over the grid when no record is open.
  const note = useSelectionParam('note');
  const openNote = (id: number) => note.open(String(id));

  return (
    <div className={`${styles.root} ${selection?.kind === 'list' ? styles.rootFlush : ''}`}>
      {selection?.kind === 'list' ? (
        // key by list id: a direct list→list change gets a fresh instance (no stale grid state
        // — groupBy/sort/filter/columns — referencing the prior list's field_keys)
        <>
          <CrmListGrid key={selection.id} listId={selection.id} onBack={sel.close} openPerson={openPerson} openOrg={openOrg} openMsg={openMsg} openNote={openNote} />
          {/* E4: a bare `note` over the grid (no record open) → the note overlay. When a record IS open
              (rec set), notes belong to the panel's own Notes sub-view (D6), so this yields to it. */}
          {note.value && !rec.value && (
            <CrmNoteOverlay noteParam={note.value} hostKind="person" hostId={0}
              onClose={note.close} onCreated={(id) => note.open(String(id), { replace: true })} />
          )}
        </>
      ) : selection?.kind === 'msg' ? (
        <CrmMessageView emailId={selection.id} onBack={sel.close} openPerson={openPerson} />
      ) : selection?.kind === 'person' ? (
        <CrmProfile
          kind="person"
          id={selection.id}
          onBack={sel.close}
          openPerson={openPerson}
          openOrg={openOrg}
          openMsgFullView={openMsgPage}
        />
      ) : selection?.kind === 'org' ? (
        <CrmProfile
          kind="organization"
          id={selection.id}
          onBack={sel.close}
          openPerson={openPerson}
          openOrg={openOrg}
          openMsgFullView={openMsgPage}
        />
      ) : (
        <CrmSearch openPerson={openPerson} openOrg={openOrg} openMsg={openMsg} openList={openList}
          openListRecord={openListRecord} />
      )}
      {/* F.3: the mail modal, over whatever is below (profile, list grid, record panel, search
          results). Suppressed on the full message page so a hand-built URL carrying both params
          cannot stack the modal on top of the page it links to. */}
      {msgId != null && selection?.kind !== 'msg' && (
        <CrmMessageModal
          emailId={msgId}
          onClose={msg.close}
          onOpenFullView={openMsgFullView}
          openPerson={openPersonFromModal}
        />
      )}
    </div>
  );
}

interface SearchProps {
  openPerson: (id: number) => void;
  openOrg: (id: number) => void;
  openMsg: (id: number) => void;
  openList: (id: number) => void;
  openListRecord: (listId: number, entryId: number) => void;
}

/** Render cap per results section (spec Part 2); the header count shows the true total and
 * "Show all N" expands in place — the full fetched set (≤50) is already client-side. */
const SECTION_CAP = 5;

/** Extra wait before the MESSAGE search fires, on top of the 300ms input debounce (~1s after typing
 * stops). Message search runs over mail bodies and costs seconds on Meili; directory and deal lookups
 * cost milliseconds, so they keep the short debounce and only the expensive search waits for the
 * user to pause — a word typed at normal speed no longer launches one body search per pause. */
const MESSAGE_SEARCH_DELAY_MS = 700;

/** Entity icons (spec Part 4) — chip-sized inline SVGs matching the Email/Meeting badge idiom. */
function OrgIcon() {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
      <path
        fill="currentColor"
        d="M2 14V2h7v4h5v8H9v-3H7v3H2zm2-2h1.5v-1.5H4V12zm0-3h1.5V7.5H4V9zm0-3h1.5V4.5H4V6zm3 3h1.5V7.5H7V9zm0-3h1.5V4.5H7V6zm4.5 4H13V8.5h-1.5V10z"
      />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
      <path
        fill="currentColor"
        d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 1.5c-2.76 0-5 1.46-5 3.5v1h10v-1c0-2.04-2.24-3.5-5-3.5z"
      />
    </svg>
  );
}

function ResultSection({ label, hint, rows }: { label: string; hint: string; rows: ReactNode[] }) {
  // Transient view state by design (nav-spec transient rule): resets when the query changes
  // (parent remounts via key={q}); not URL state, so Back/reload land on the collapsed view.
  const [expanded, setExpanded] = useState(false);
  if (rows.length === 0) return null;
  return (
    <>
      <div className={styles.groupLabel}>
        {label} · {rows.length} <span className={styles.groupHint}>— {hint}</span>
      </div>
      {expanded ? rows : rows.slice(0, SECTION_CAP)}
      {rows.length > SECTION_CAP && (
        <button className={styles.showMoreRow} onClick={() => setExpanded((e) => !e)}>
          {expanded ? 'Show fewer ↑' : `Show all ${rows.length} ↓`}
        </button>
      )}
    </>
  );
}

function CrmSearch({ openPerson, openOrg, openMsg, openList, openListRecord }: SearchProps) {
  const [q, setQ] = useViewParam('q', '', { history: 'replace' });
  const [input, setInput] = useState(q);

  // resync input when the URL q changes underneath us (Back, deep link, tab re-entry) —
  // the adjust-state-during-render pattern (PrivacySection precedent), not an effect.
  const [lastQ, setLastQ] = useState(q);
  if (q !== lastQ) {
    setLastQ(q);
    setInput(q);
  }

  // debounce input → URL (the URL value is what the queries key on)
  useEffect(() => {
    const t = setTimeout(() => setQ(input), 300);
    return () => clearTimeout(t);
  }, [input, setQ]);

  // Flush the pending debounced write BEFORE pushing a selection entry, so ?q= lands on
  // the list's history entry (same-tick writes compose; writeParam skips when unchanged).
  const flushThen = (fn: () => void) => {
    setQ(input);
    fn();
  };

  // Focus the box only on a FRESH arrival (no restored query). CrmSearch remounts on every
  // Back/close from a selection — an unconditional autoFocus would steal focus and fight
  // the browser's popstate scroll restoration on long lists; preventScroll keeps the
  // restored offset either way.
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [focusOnMount] = useState(() => q === '');
  useEffect(() => {
    if (focusOnMount) inputRef.current?.focus({ preventScroll: true });
  }, [focusOnMount]);

  // The message query keys on its own, later copy of q. It starts EQUAL to q so a restored query
  // (Back, reload, deep link) searches messages immediately; only a fresh edit waits for the pause.
  const [messageQ, setMessageQ] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setMessageQ(q), MESSAGE_SEARCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [q]);

  const directory = useCrmSearch('directory', q);
  const content = useCrmSearch('content', messageQ);
  const opportunities = useCrmOpportunitySearch(q);

  const dirHits = (directory.data?.hits ?? []) as unknown as CrmDirectoryHit[];
  const people = dirHits.filter((h) => h.entity_kind === 'person' && isPersonSubtype(h.subtype));
  // distribution lists / shared / automated mailboxes — tracked but shown separately, lower priority
  const lists = dirHits.filter((h) => h.entity_kind === 'person' && !isPersonSubtype(h.subtype));
  const orgs = dirHits.filter((h) => h.entity_kind === 'organization');
  const opps = (opportunities.data?.results ?? []).filter(
    (r): r is CrmLookupResult & { list_id: number } => r.kind === 'opportunity' && r.list_id != null,
  );
  const messages = ((content.data?.hits ?? []) as unknown as CrmContentHit[]).filter(
    (h) => h.content_kind === 'email' || h.content_kind === 'meeting',
  );

  // Best-match ordering (search-quality spec Part 2 amendment): Organizations, People and Opportunities
  // render in the order of the group holding the best NAME match; ties keep the spec's default order.
  // Deals are also ranked within their group — the SQL lookup returns them in record-id order.
  const query = q.trim();
  const rankedOpps = opps
    .map((r) => ({ r, tier: matchTier(r.name, query) }))
    .sort((a, b) => a.tier - b.tier);
  const entityGroups = orderByBestMatch([
    { key: 'orgs', tiers: orgs.map((h) => rowTier([h.primary_name, h.domain], query)) },
    { key: 'people', tiers: people.map((h) => rowTier([h.primary_name, h.primary_email], query)) },
    { key: 'opps', tiers: rankedOpps.map((x) => x.tier) },
  ]);

  const active = q.trim().length >= 2;
  // Results render in two stages. The fast lookups — the directory search and the SQL deal lookup —
  // gate the page together, so the best-match group order is settled before anything paints (no
  // section jumps when the second one lands). Messages stream in below when their slower search
  // lands. While messageQ still trails q, the content hook holds the PREVIOUS query's hits, so that
  // window counts as pending too — never show stale messages.
  const loading = active && (directory.isLoading || opportunities.isLoading);
  const messagesPending = active && (messageQ.trim() !== q.trim() || content.isLoading);
  const searchDisabled = [directory.error, content.error].some(
    (e) =>
      e instanceof ApiError &&
      e.status === 400 &&
      /disabled|crm_search_enabled/i.test(String((e.detail as { detail?: string } | null)?.detail ?? '')),
  );
  // Each surface fails on its own: a directory failure is the page-level error; a message failure
  // degrades only the Messages section (as the SQL opportunity lookup degrades only its own).
  const otherError = !searchDisabled && active && directory.error != null;
  const messagesError = !searchDisabled && active && !messagesPending && content.error != null;
  const empty =
    active && !loading && !messagesPending && !searchDisabled && !otherError && !messagesError &&
    people.length === 0 && orgs.length === 0 && lists.length === 0 &&
    opps.length === 0 && messages.length === 0;

  return (
    <>
      <div className={styles.header}>
        <h1 className={styles.title}>CRM</h1>
        <div className={styles.subtitle}>
          governed search · people, organizations, deals &amp; messages · read-only prove-out
        </div>
        <div className={styles.searchBar}>
          <input
            ref={inputRef}
            className={styles.searchInput}
            type="search"
            placeholder="Search people, orgs, deals & messages…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
        </div>
      </div>

      {!active && <CrmListsHome openList={openList} />}

      {!active && (
        <div className={styles.empty}>
          Type at least 2 characters — results are your entitled set only.
        </div>
      )}
      {searchDisabled && (
        <div className={styles.notice}>
          Governed search is disabled (crm_search_enabled=false) — ask an admin to enable it.
        </div>
      )}
      {otherError && <div className={styles.error}>Search failed — try again.</div>}
      {loading && <div className={styles.loading}>Searching…</div>}

      {active && !loading && !searchDisabled && (
        // key={q}: a query change remounts the sections so per-section expansion resets (transient).
        // Orgs / People / Opportunities in best-match order (default Orgs → People → Opportunities),
        // then Lists → Messages, fixed (search-quality spec Part 2 + its amendment).
        <div key={q.trim()}>
          {empty && <div className={styles.empty}>No entitled matches for “{q.trim()}”.</div>}

          {entityGroups.map(({ key }) => (
            <Fragment key={key}>
              {key === 'orgs' ? (
                <ResultSection
                  label="Organizations"
                  hint="directory"
                  rows={orgs.map((h) => (
                    <button
                      key={h.doc_id}
                      className={styles.resultRow}
                      onClick={() => flushThen(() => openOrg(h.entity_id))}
                    >
                      <span className={`${styles.chip} ${styles.iconChipOrg}`} title="organization"><OrgIcon /></span>
                      <span className={styles.resultName}>{h.primary_name || '(unnamed)'}</span>
                      <span className={styles.resultMeta}>{h.domain || ''}</span>
                    </button>
                  ))}
                />
              ) : key === 'people' ? (
                <ResultSection
                  label="People"
                  hint="directory"
                  rows={people.map((h) => {
                    const hasStats = (h.emails_in ?? 0) + (h.emails_out ?? 0) + (h.meetings ?? 0) > 0;
                    return (
                      <button
                        key={h.doc_id}
                        className={styles.resultRow}
                        onClick={() => flushThen(() => openPerson(h.entity_id))}
                      >
                        <span className={`${styles.chip} ${styles.iconChipPerson}`} title="person"><PersonIcon /></span>
                        <span className={styles.resultName}>{h.primary_name || '(unnamed)'}</span>
                        <span className={styles.resultMeta}>
                          {[h.primary_email && `(${h.primary_email})`, h.title, h.org_name]
                            .filter(Boolean).join(' · ')}
                        </span>
                        <span className={styles.rowRight}>
                          {hasStats && (
                            <>
                              <span className={styles.statChip} title="emails sent to them">E↑ {h.emails_out ?? 0}</span>
                              <span className={styles.statChip} title="emails received from them">E↓ {h.emails_in ?? 0}</span>
                              <span className={styles.statChip} title="meetings together">M {h.meetings ?? 0}</span>
                            </>
                          )}
                          {h.last_contact_at != null && (
                            <span className={styles.resultDate} title="last contact">{fmtShortDate(h.last_contact_at)}</span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                />
              ) : (
                <ResultSection
                  label="Opportunities"
                  hint="deal records on lists you can see"
                  rows={rankedOpps.map(({ r }) => (
                    <button
                      key={`opp_${r.id}`}
                      className={styles.resultRow}
                      onClick={() => flushThen(() => openListRecord(r.list_id, r.id))}
                    >
                      <span className={`${styles.chip} ${styles.chipCurrent}`}>Deal</span>
                      <span className={styles.resultName}>{r.name || '(unnamed)'}</span>
                      <span className={styles.resultMeta}>{r.list_name ?? ''}</span>
                    </button>
                  ))}
                />
              )}
            </Fragment>
          ))}

          <ResultSection
            label="Lists & shared mailboxes"
            hint="distribution lists, shared & automated mailboxes"
            rows={lists.map((h) => (
              <button
                key={h.doc_id}
                className={styles.resultRow}
                onClick={() => flushThen(() => openPerson(h.entity_id))}
              >
                <span className={`${styles.chip} ${styles.chipMuted}`}>{subtypeLabel(h.subtype)}</span>
                <span className={styles.resultName}>{h.primary_name || '(unnamed)'}</span>
                <span className={styles.resultMeta}>{h.permission_class}</span>
              </button>
            ))}
          />

          {messagesPending || messagesError ? (
            <>
              <div className={styles.groupLabel}>Messages</div>
              {messagesPending
                ? <div className={styles.loading}>Searching messages…</div>
                : <div className={styles.error}>Message search failed — try again.</div>}
            </>
          ) : (
            <ResultSection
              label="Messages"
              hint="email + meeting subject/body, threads you're entitled to (notes, files → later)"
              rows={messages.map((h) =>
                h.content_kind === 'email' ? (
                  <button
                    key={h.doc_id}
                    className={styles.resultRow}
                    onClick={() => flushThen(() => openMsg(h.content_id))}
                  >
                    <span className={`${styles.chip} ${styles.kindEmail}`}>Email</span>
                    <span className={styles.resultName}>{h.subject || '(no subject)'}</span>
                    <span className={styles.resultDate}>{fmtShortDate(h.sent_at ?? null)}</span>
                  </button>
                ) : (
                  <div key={h.doc_id} className={`${styles.resultRow} ${styles.resultRowStatic}`}>
                    <span className={`${styles.chip} ${styles.kindMeeting}`}>Meeting</span>
                    <span className={styles.resultName}>{h.subject || '(no subject)'}</span>
                    <span className={styles.resultDate}>{fmtShortDate(h.sent_at ?? null)}</span>
                  </div>
                ),
              )}
            />
          )}
        </div>
      )}
    </>
  );
}
